import { IWorkflowRestrictions } from '~/client/src/shared/types/ILocationAttributeDto'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'

// Single source of truth for the company-restriction decision used by both
// the resource pickers (row disabling) and form/save validation.
//
// Bookable resources (equipment, materials, …) must take company-restriction
// from the project resource store, registered into the form store as
// freshResourcePermittedCompanies. The legacy LocationAttributesStore can be
// stale for permittedCompanies, so these resources use the fresh map exclusively
// and fail closed when it isn't
// available yet. Other location types keep using the legacy attr.

export interface ICompanyRestrictionSource {
  // Whether this location resolves company restrictions through the fresh
  // resource map (a bookable resource type) rather than the legacy attr.
  usesFreshSource: boolean
  // Whether the fresh resource data has been registered into the store.
  isFreshDataReady: boolean
  // Whether this id is present in the fresh resource map.
  isInFreshMap: boolean
  // Permitted company ids from the fresh map (null when absent from the map).
  freshPermitted: string[] | null
  // Permitted company ids from the legacy attr (used for location types not
  // synced into the fresh map).
  legacyPermitted: string[] | null
}

// Mirrors LocationBase.hasRestrictionForCompanies: an empty/absent permitted
// list means "no restriction", and a non-empty list restricts when any of the
// permit's companies is not in it. No permit companies => no restriction
// (matches the prior getRestrictedLocationAttr short-circuit).
export const isRestrictedByPermittedCompanies = (
  permittedCompanies: string[] | null | undefined,
  companyIds: string[] | null | undefined,
): boolean => {
  if (!companyIds?.length) return false
  if (!permittedCompanies?.length) return false
  return companyIds.some(id => !permittedCompanies.includes(id))
}

export const isLocationCompanyRestricted = (
  companyIds: string[] | null | undefined,
  source: ICompanyRestrictionSource,
): boolean => {
  // No permit companies selected => nothing to restrict against.
  if (!companyIds?.length) return false

  if (source.usesFreshSource) {
    // Fail closed until the fresh source is ready, and for any resource id
    // missing from it (e.g. selected-but-not-in-the-v2-result-set on an edit
    // flow). Admin/forms-master callers bypass this check separately.
    if (!source.isFreshDataReady) return true
    if (!source.isInFreshMap) return true
    return isRestrictedByPermittedCompanies(source.freshPermitted, companyIds)
  }

  return isRestrictedByPermittedCompanies(source.legacyPermitted, companyIds)
}

interface IPermittedCompaniesRecord {
  id: string
  root?: { permittedCompanies?: string[] | null } | null
}

// Builds the locationId -> permittedCompanies map registered into the store for
// every synced bookable resource type. v2 null/undefined permittedCompanies
// means "no restriction configured", so it maps to [] — every returned id
// becomes "known" and only ids genuinely missing from the result set fail
// closed in the store.
export const buildResourcePermittedCompaniesMap = (
  records: IPermittedCompaniesRecord[],
): Map<string, string[]> => {
  const map = new Map<string, string[]>()
  for (const record of records) {
    map.set(record.id, record.root?.permittedCompanies ?? [])
  }
  return map
}

interface IResourceControlRecord {
  id: string
  root?: { isBookable?: boolean | null } | null
}

// Builds the locationId -> bookable/workflow-type control map registered into
// the store, so the form field and save validation can resolve a resource's
// controls by id. Absent fields default to "not bookable / no workflow types".
export const buildResourceControlFieldsMap = (
  records: IResourceControlRecord[],
): Map<string, { isBookable: boolean }> => {
  const map = new Map<string, { isBookable: boolean }>()
  for (const record of records) {
    map.set(record.id, {
      isBookable: record.root?.isBookable ?? false,
    })
  }
  return map
}

interface IWorkflowRestrictionsRecord {
  id: string
  root?: { workflowRestrictions?: IWorkflowRestrictions | null }
}

// Builds the locationId -> workflowRestrictions map registered into the store
// for resources absent from the legacy LocationAttributesStore (e.g. materials),
// so max-booking-duration validation can resolve their restrictions by id.
export const buildResourceWorkflowRestrictionsMap = (
  records: IWorkflowRestrictionsRecord[],
): Map<string, IWorkflowRestrictions> => {
  const map = new Map<string, IWorkflowRestrictions>()
  for (const record of records) {
    const restrictions = record.root?.workflowRestrictions
    if (restrictions) map.set(record.id, restrictions)
  }
  return map
}

interface IOperatingIntervalsRecord {
  id: string
  root?: { operatingIntervals?: IMsDateInterval[] | null } | null
}

// Builds the resource-id -> hours-of-operation map registered into the store so
// the form can warn when a workflow's time period falls outside a resource's
// operating hours. Only fed for resource types that enforce operating hours
// (equipment today); records with no intervals are omitted (no constraint).
export const buildResourceOperatingIntervalsMap = (
  records: IOperatingIntervalsRecord[],
): Map<string, IMsDateInterval[]> => {
  const map = new Map<string, IMsDateInterval[]>()
  for (const record of records) {
    const intervals = record.root?.operatingIntervals
    if (intervals?.length) map.set(record.id, intervals)
  }
  return map
}

// Permitted-company ids to name in the "Reserved for: …" message, or null when
// the restriction is pessimistic (fresh resource data still loading, or an
// unknown resource id) and there is no concrete permitted list to display.
export const getPermittedCompaniesToName = (
  source: ICompanyRestrictionSource,
): string[] | null =>
  source.usesFreshSource ? source.freshPermitted : source.legacyPermitted
